import { Cross2Icon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectionEdit } from '@/features/projects/project-details/hooks/use-connection-edit';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { ProjectWithContractors } from '@/shared/types/api';

type EditConnectionDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  connectedProjectId: number | null;
  projects: ProjectWithContractors[] | undefined;
  isLoading: boolean;
  onSelect: (projectId: number | null) => void;
  children: ReactNode;
};

export const EditConnectionDialog = ({
  isOpen,
  onOpenChange,
  projectId,
  connectedProjectId,
  projects,
  isLoading,
  onSelect,
  children,
}: EditConnectionDialogProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'ProjectConnection' });
  const { t: tShared } = useTranslation('project', { keyPrefix: 'Shared' });

  const { availableProjects, handleSelectProject } = useConnectionEdit({
    projectId,
    projects,
    onSelect,
    onOpenChange,
  });

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="center"
          sideOffset={8}
          className="z-50 w-[286px]"
        >
          <div
            className="relative rounded-[16px] border-[0.5px] border-transparent"
            style={{
              background:
                'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(112.383deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <div className="rounded-[16px] bg-transparent p-[8px]">
              {/* Title Container */}
              <div className="mb-[8px] flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
                <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                  {t('connection')}
                </p>
                <button
                  onClick={() => onOpenChange(false)}
                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
                >
                  <Cross2Icon className="size-4 text-white" />
                  <span className="sr-only">Close</span>
                </button>
              </div>

              {/* Divider */}
              <div className="mb-[8px] h-[1px] w-full bg-gradient-to-r from-white/50 to-transparent" />

              {/* Content */}
              <div className="space-y-[24px] px-[8px] pb-[8px]">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingIndicator className="size-10" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-[12px]">
                    {/* No Connection Option */}
                    <div
                      className="flex w-full cursor-pointer items-center justify-between py-0 pl-0 pr-[8px] text-[13px] text-[#fefefe] transition-opacity hover:opacity-80"
                      onClick={() => handleSelectProject(null)}
                    >
                      <p className="whitespace-nowrap text-[13px] font-normal leading-[18px] tracking-[-0.4px]">
                        {t('no_connection')}
                      </p>
                      {connectedProjectId === null && (
                        <div className="flex flex-col justify-center leading-[0]">
                          <p className="leading-[normal]">􀆅</p>
                        </div>
                      )}
                    </div>

                    {/* Label */}
                    <div className="flex items-center gap-[4px]">
                      <p className="text-[11px] font-normal leading-[13px] tracking-[-0.4px] text-[#cecece]">
                        {tShared('all_projects')}
                      </p>
                    </div>

                    {/* Project List */}
                    <div className="relative">
                      <div className="flex max-h-[200px] flex-col gap-[12px] overflow-y-auto pr-2">
                        {/* Available Projects */}
                        {availableProjects.map((project) => (
                          <div
                            key={project.id}
                            className="flex w-full cursor-pointer items-center justify-between py-0 pl-0 pr-[8px] text-[13px] text-[#fefefe] transition-opacity hover:opacity-80"
                            onClick={() => handleSelectProject(project.id)}
                          >
                            <p className="whitespace-nowrap text-[13px] font-normal leading-[18px] tracking-[-0.4px]">
                              {project.name}
                            </p>
                            {connectedProjectId === project.id && (
                              <div className="flex flex-col justify-center leading-[0]">
                                <p className="leading-[normal]">􀆅</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Custom Scrollbar Indicator */}
                      {availableProjects.length > 5 && (
                        <div className="absolute right-0 top-0 h-[100px] w-[5px] rounded-[16px] bg-[rgba(217,217,217,0.2)]" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
